/* eslint-disable jira/js/no-reduce-accumulator-spread */

import flatMap from 'lodash/flatMap';
import flattenDeep from 'lodash/flattenDeep';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import max from 'lodash/max';
import merge from 'lodash/merge';
import sortedUniq from 'lodash/sortedUniq';
import uniqBy from 'lodash/uniqBy';
import { ApolloError } from 'apollo-client';
import { isEqual, isBefore, isAfter, parseISO, startOfWeek, add } from 'date-fns';
import { graphql, fetchQuery } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { getHttpStatusCodeGroup } from '@atlassian/jira-fetch/src/utils/http-status-code.tsx';
import { createAri, getAriConfig, isAri, toAri } from '@atlassian/jira-platform-ari';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import getRelayEnvironment from '@atlassian/jira-relay-environment';
import type {
	Rotation,
	Recipient,
	ExperimentApiResponse,
	ScheduleItem,
	BulkUsersResponse,
} from './types';

export const getStatusCodeFromApolloError = (error: ApolloError) => {
	// Check if GraphQL Errors exist in ApolloError and that it is an array
	const graphQLErrors = get(error, 'graphQLErrors');
	if (!graphQLErrors || !Array.isArray(graphQLErrors)) {
		return null;
	}
	// Extract all the status codes from the GraphQL errors
	// https://developer.atlassian.com/platform/graphql-gateway/standards/error-categorisation/
	const statusCodes = graphQLErrors
		.map((err) => get(err, ['extensions', 'statusCode']))
		.filter((statusCode) => !!statusCode);
	// Get the top status code from all the errors
	const statusCode = max(statusCodes);
	return statusCode || null;
};

export const generateStatusCodeAttribute = (error: Error) => {
	// If an Apollo Error is captured, get the status code from the ApolloError and
	// send it to jira-fetch's getHttpStatusCodeGroup to populate statusCodeGroup
	if (error instanceof ApolloError) {
		const statusCode = getStatusCodeFromApolloError(error);
		const statusCodeGroup = !statusCode ? 'unknown' : getHttpStatusCodeGroup(statusCode);
		return { statusCodeGroup };
	}
	// Else, return an empty object to satsify object merging
	return {};
};

export const isBetween = (date: Date, from: string, to: string, inclusivity = '()'): boolean => {
	let include = inclusivity;
	if (!['()', '[]', '(]', '[)'].includes(inclusivity)) {
		include = '()';
	}

	const isBeforeEqual = include[0] === '[';
	const isAfterEqual = include[1] === ']';

	return (
		(isBeforeEqual
			? isEqual(parseISO(from), date) || isBefore(parseISO(from), date)
			: isBefore(parseISO(from), date)) &&
		(isAfterEqual
			? isEqual(parseISO(to), date) || isAfter(parseISO(to), date)
			: isAfter(parseISO(to), date))
	);
};

export const getOnCallRecipients = (rotation: Rotation): Recipient[] => {
	const currentPeriod =
		(rotation.periods &&
			rotation.periods.filter((period) =>
				isBetween(new Date(), period.startDate, period.endDate, '[)'),
			)) ||
		[];

	return currentPeriod.map((period) => period.recipient);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformAggResult = (data: any): ExperimentApiResponse => {
	// If schedules is null we assume the user has no account on the configured
	// opsgenie instance. This detail was lost when converting from Rest to AGG.
	if (data.opsgenie.opsgenieTeam.schedules == null) {
		throw new FetchError(
			403,
			'"opsgenieTeam.schedules" was null in GraphQL response, assuming the user has no account on the instance',
		);
	}

	// @ts-expect-error - TS7006 - Parameter 'schedule' implicitly has an 'any' type.
	return data.opsgenie.opsgenieTeam.schedules.filter(Boolean).map((schedule) => {
		// @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
		const ari = getAriConfig(toAri(data.opsgenie.opsgenieTeam.id));
		return {
			schedule: {
				id: schedule.id,
				name: schedule.name,
				teamId: ari.resourceId,
				enabled: schedule.enabled,
				timezone: schedule.timezone,
			},
			startDate: schedule.finalTimeline.startDate,
			endDate: schedule.finalTimeline.endDate,
			finalTimeline: {
				rotations: (schedule.finalTimeline.rotations || [])
					.filter(Boolean)
					// @ts-expect-error - TS7006 - Parameter 'rotation' implicitly has an 'any' type.
					.map((rotation) => ({
						id: rotation.id,
						name: rotation.name,
						order: rotation.order,
						// @ts-expect-error - TS7006 - Parameter 'period' implicitly has an 'any' type.
						periods: (rotation.periods || []).filter(Boolean).map((period) => ({
							startDate: period.startDate,
							endDate: period.endDate,
							type: period.type,
							recipient: {
								type: period.recipient.type,
								accountId: period.recipient.user?.accountId,
								id: period.recipient.user?.accountId,
								name: period.recipient.user?.name,
							},
						})),
					})),
			},
		};
	});
};

const fetchScheduleFromAGG = async (
	baseUrl: string,
	cloudId: string,
	teamId: string,
	analyticsEvent?: UIAnalyticsEvent,
) => {
	const start = startOfWeek(new Date(), { weekStartsOn: 1 });
	const startDate = start.toISOString();
	const endDate = add(start, { weeks: 2 }).toISOString();

	let schedulesResponse;
	try {
		schedulesResponse = await fetchQuery(
			getRelayEnvironment(),
			graphql`
				query utilsOpsgenieTeamScheduleQuery(
					$teamId: ID!
					$startTime: DateTime!
					$endTime: DateTime!
				) {
					opsgenie @required(action: THROW) {
						opsgenieTeam(id: $teamId) @required(action: THROW) {
							id @required(action: THROW)
							schedules {
								id @required(action: THROW)
								name @required(action: THROW)
								enabled @required(action: THROW)
								timezone @required(action: THROW)
								finalTimeline(startTime: $startTime, endTime: $endTime) @required(action: THROW) {
									startDate @required(action: THROW)
									endDate @required(action: THROW)
									rotations {
										id @required(action: THROW)
										name @required(action: THROW)
										order @required(action: THROW)
										periods {
											startDate @required(action: THROW)
											endDate @required(action: THROW)
											type @required(action: THROW)
											recipient @required(action: THROW) {
												type @required(action: THROW)
												user {
													accountId @required(action: THROW)
													name @required(action: THROW)
												}
											}
										}
									}
								}
							}
						}
					}
				}
			`,
			{
				teamId: isAri(teamId)
					? teamId
					: createAri({
							resourceOwner: 'opsgenie',
							cloudId,
							resourceType: 'team',
							resourceId: teamId,
						}),
				startTime: startDate,
				endTime: endDate,
			},
		).toPromise();

		if (!schedulesResponse) {
			throw new Error('API call failed');
		}
		schedulesResponse = transformAggResult(schedulesResponse);

		analyticsEvent &&
			fireOperationalAnalytics(analyticsEvent, 'opsgenieSchedulesForOncall fetched', {
				backend: 'agg',
			});
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		analyticsEvent &&
			fireErrorAnalytics({
				error,
				meta: {
					packageName: 'jiraPlatformOpsgenieSchedule',
					id: 'opsgenieSchedulesForOncall',
				},
				event: analyticsEvent,
				sendToPrivacyUnsafeSplunk: true,
				attributes: {
					backend: 'agg',
					error: error.message,
					// Will either return an object with the GraphQL status code group or
					// an empty object. That way, if the error is not from GraphQL and
					// does have a status code, it will still be captured.
					...generateStatusCodeAttribute(error),
				},
			});
		throw error;
	}

	return schedulesResponse;
};

export const getName = (
	{
		accountId,
		name,
		type,
		aaidFullnames,
	}: {
		accountId?: string | null;
		name?: string | null;
		type: string;
		aaidFullnames: {
			[key: string]: string;
		};
	},
	unknownUserName: string,
) => {
	if (type === 'noone') {
		return '--';
	}
	if (!isNil(accountId) && !isEmpty(aaidFullnames[accountId])) {
		return aaidFullnames[accountId];
	}
	if (!isNil(name) && !isEmpty(name)) {
		return name;
	}

	return unknownUserName;
};

export const getAccountIds = (schedulesRes: ExperimentApiResponse): string[] =>
	// eslint-disable-next-line @atlassian/eng-health/code-evolution/ts-migration-4.9-5.4 -- PLEASE FIX THIS SUPPRESSION
	// @ts-expect-error([Part of upgrade 4.9-5.4]) - Type '(string | null | undefined)[]' is not assignable to type 'string[]'.
	sortedUniq(
		flattenDeep(
			schedulesRes.map((timeline) =>
				timeline.finalTimeline.rotations
					.filter((rotation) => rotation.periods)
					.map((rotation) =>
						rotation.periods
							.filter((period) => period.recipient.accountId)
							.map((period) => period.recipient.accountId),
					),
			),
		),
	);

export const fetchUsersWithAccountIds = async (
	accountIds: string[],
	baseUrl: string,
	analyticsEvent?: UIAnalyticsEvent,
): Promise<BulkUsersResponse> => {
	// IE has a maximum of 2083 chars for URL length.
	// Each accountID requires 34 chars, 59 Ids roughly equal to 2000
	// 59 users should be sufficient, anything more than that would make the schedules unreadable anyway.
	// https://support.microsoft.com/en-gb/help/208427/maximum-url-length-is-2-083-characters-in-internet-explorer
	const maxNumOfAccountIds = 59;
	const accountIdsQs = accountIds // eslint-disable-next-line @typescript-eslint/no-explicit-any
		.reduce<Array<any>>((unique, item) => (unique.includes(item) ? unique : [...unique, item]), []) // Dedup account ids
		.slice(0, maxNumOfAccountIds)
		.map((id) => `accountId=${id}`)
		.join('&');
	const api = `${baseUrl}/rest/api/2/user/bulk?maxResults=${maxNumOfAccountIds}&${accountIdsQs}`;
	let response;
	try {
		response = await fetchJson(api);
		analyticsEvent && fireOperationalAnalytics(analyticsEvent, 'accountNamesForOncall fetched');
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		analyticsEvent &&
			fireErrorAnalytics({
				meta: {
					id: 'accountNamesForOncall',
					packageName: 'jiraPlatformOpsgenieSchedule',
				},
				error,
				event: analyticsEvent,
				sendToPrivacyUnsafeSplunk: true,
			});
		throw error;
	}
	return response;
};

export const getScheduleByTeam = async (
	unknownUserName: string,
	baseUrl: string,
	cloudId: string,
	teamId: string,
	analyticsEvent?: UIAnalyticsEvent,
): Promise<ScheduleItem[]> => {
	const schedulesResponse = await fetchScheduleFromAGG(baseUrl, cloudId, teamId, analyticsEvent);

	// ensure all schedules have finalTimeline.rotations
	const schedulesWithTimeLine = schedulesResponse.map((timeline) =>
		merge({ finalTimeline: { rotations: [] } }, timeline),
	);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const aaidFullnames: Record<string, any> = {};

	// @ts-expect-error - TS2322 - Type '{ schedule: ScheduleMeta; onCallRecipients: boolean[]; startDate: string; endDate: string; finalTimeline: { rotations: any[]; }; }[]' is not assignable to type 'ScheduleItem[]'.
	const schedules: ScheduleItem[] = schedulesWithTimeLine.map((timeline) => {
		// replace accountId address with full names
		const finalTimeline = {
			rotations: timeline.finalTimeline.rotations.map((rotation) => ({
				// @ts-expect-error - TS2698 - Spread types may only be created from object types.
				...rotation,
				periods:
					// @ts-expect-error - TS2339 - Property 'periods' does not exist on type 'never'.
					(rotation.periods &&
						// @ts-expect-error - TS2339 - Property 'periods' does not exist on type 'never'. | TS7006 - Parameter 'period' implicitly has an 'any' type.
						rotation.periods.map((period) => ({
							...period,
							recipient: {
								...period.recipient,
								name: getName(
									{
										accountId: period.recipient.accountId,
										name: period.recipient.name,
										type: period.recipient.type,
										aaidFullnames,
									},
									unknownUserName,
								),
							},
						}))) ||
					[],
			})),
		};

		// assign onOnCallRecipients
		const onCallRecipients = flatMap(finalTimeline.rotations, getOnCallRecipients);
		const dedupedRecipients = uniqBy(onCallRecipients, 'id');

		const schedule = {
			schedule: timeline.schedule,
			onCallRecipients: dedupedRecipients,
			startDate: timeline.startDate,
			endDate: timeline.endDate,
			finalTimeline,
		};

		return schedule;
	});

	return schedules;
};
